/**
 * Analytics cache slice — memoises Percento results keyed by
 * `${mode}:${period.start}:${period.end}:${currency}:${book_id}` (caller's
 * choice; the slice is key-agnostic).
 *
 * `clear()` is meant to be called whenever the underlying transactions
 * slice mutates (the wave 13 store wires that side-effect via subscribe).
 */

import type { PercentoResult } from '@/lib/analytics/percento';

export interface AnalyticsCacheState {
  cache: Record<string, PercentoResult>;
}

export interface AnalyticsCacheActions {
  get: (key: string) => PercentoResult | undefined;
  set: (key: string, result: PercentoResult) => void;
  clear: () => void;
}

export type AnalyticsCacheSlice = AnalyticsCacheState & AnalyticsCacheActions;

export const initialAnalyticsCacheState: AnalyticsCacheState = {
  cache: {},
};

export const createAnalyticsCacheSlice =
  <T extends AnalyticsCacheSlice>() =>
  (
    set: (
      partial: Partial<T> | ((state: T) => Partial<T>),
      replace?: boolean,
    ) => void,
    get?: () => T,
  ): AnalyticsCacheSlice => {
    let snapshot: Record<string, PercentoResult> = {};
    return {
      ...initialAnalyticsCacheState,

      get: (key) => {
        const cache = get ? get().cache : snapshot;
        return cache[key];
      },

      set: (key, result) =>
        set((state) => {
          const next = { ...state.cache, [key]: result };
          snapshot = next;
          return { cache: next } as Partial<T>;
        }),

      clear: () => {
        snapshot = {};
        set({ cache: {} } as Partial<T>);
      },
    };
  };
